/**
 * Why a call has no notes, in words (2026-10-22 "Calls are not creating notes":
 * every case used to read "No notes"). Staff only — members never see notes.
 *
 * `reason` comes from the backend (`notes_reason` on the call, the call message
 * and the Calls & notes rows); `error` is a failed call's recorded reason.
 *
 * TRUS-708: `waiting_for_configuration` (notes_status stays pending) and the
 * recording-deleted line for a failed call whose media is gone.
 */

import type { CallNotesReason } from '@/services/callsApi';

type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

export interface CallNotesReasonText {
  /** The status line ("Not recorded", "Notes failed: …"). */
  label: string;
  /** How to get notes next time, when there is something staff can do. */
  hint: string | null;
}

export function callNotesReasonText(
  t: TFn,
  reason: CallNotesReason | null | undefined,
  error?: string | null,
): CallNotesReasonText | null {
  switch (reason) {
    case 'in_progress':
      return { label: t('messaging.calls.notesInProgress', 'Call in progress · notes are made when it ends'), hint: null };
    case 'waiting_for_configuration':
      // TRUS-708: the worker is missing a key; the recording is kept and the
      // notes are made as soon as it is configured. Nothing for staff to do.
      return {
        label: t('messaging.calls.notesWaitingConfig', 'Waiting for configuration — notes will be generated automatically'),
        hint: null,
      };
    case 'failed':
      return {
        label: error
          ? t('messaging.calls.notesFailedReason', 'Notes failed: {{reason}}', { reason: error })
          : t('messaging.calls.notesFailed', 'Notes failed'),
        hint: null,
      };
    case 'not_processed':
      return { label: t('messaging.calls.notesNotProcessed', 'Recorded, but the notes were never started'), hint: null };
    case 'notes_off':
      return { label: t('messaging.calls.notesOffForOrg', 'Notes are off for this organization'), hint: null };
    case 'not_configured':
      return {
        label: t('messaging.calls.notesNotConfigured', "Notes aren't set up: transcription isn't configured"),
        hint: null,
      };
    case 'members_not_allowed':
      return {
        label: t('messaging.calls.notesMembersNotAllowed', "Not recorded: calls with members can't be recorded here"),
        hint: null,
      };
    case 'not_recorded':
      return {
        label: t('messaging.calls.callNotRecorded', 'Not recorded'),
        hint: t('messaging.calls.notRecordedHint', 'To get notes, choose Start recording in the call controls.'),
      };
    default:
      return null;
  }
}

/** A failed call whose recording was deleted: notes can never be made (TRUS-708). */
export function callNotesRecordingGoneText(t: TFn): string {
  return t('messaging.calls.notesRecordingDeleted', "The recording is no longer available, so these notes can't be made.");
}
